import React, { useState, useRef, useEffect, useCallback } from "react";
import api, { pollJob } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ArrowRight, Play, Pause, Square, Mic, ShieldCheck, Radio, Activity, AlertTriangle, Pin, Monitor, Loader2 } from "lucide-react";

const TYPE_STYLE = {
  requirement: "border-l-[#29abe2] bg-[#29abe2]/5",
  decision: "border-l-emerald-500 bg-emerald-500/5",
  risk: "border-l-red-500 bg-red-500/5",
  action: "border-l-[#c9a227] bg-[#c9a227]/5",
  question: "border-l-purple-500 bg-purple-500/5",
};

function detectInsight(text) {
  const t = text.toLowerCase();
  if (t.includes("?")) return { type: "question", text };
  if (/\b(risk|concern|worried|blocker|issue)\b/.test(t)) return { type: "risk", text };
  if (/\b(decide|decision|agreed|we'll go|let's use)\b/.test(t)) return { type: "decision", text };
  if (/\b(action|follow up|will send|to do|assign)\b/.test(t)) return { type: "action", text };
  if (/\b(need|require|must|should|want|expect)\b/.test(t)) return { type: "requirement", text };
  return null;
}

function Gauge({ score }) {
  const color = score >= 80 ? "#10b981" : score >= 55 ? "#c9a227" : "#ef4444";
  const r = 42, c = 2 * Math.PI * r, off = c - (score / 100) * c;
  return (
    <div className="relative w-28 h-28">
      <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
        <circle cx="50" cy="50" r={r} fill="none" stroke="hsl(var(--border))" strokeWidth="8" />
        <circle cx="50" cy="50" r={r} fill="none" stroke={color} strokeWidth="8" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={off} style={{ transition: "stroke-dashoffset .6s ease" }} />
      </svg>
      <div className="absolute inset-0 grid place-items-center"><div className="text-center"><div className="text-2xl font-bold text-foreground">{score}</div><div className="text-[9px] text-muted-foreground uppercase tracking-wider">Quality</div></div></div>
    </div>
  );
}

export default function RecordStep({ meeting, setActive, refresh, mid }) {
  const [consent, setConsent] = useState(false);
  const [withTab, setWithTab] = useState(false);
  const [state, setState] = useState("idle");
  const [elapsed, setElapsed] = useState(0);
  const [lines, setLines] = useState([]);
  const [interim, setInterim] = useState("");
  const [insights, setInsights] = useState([]);
  const [level, setLevel] = useState(0);
  const [score, setScore] = useState(90);
  const [spark, setSpark] = useState([]);
  const [chunks, setChunks] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [speechOk, setSpeechOk] = useState(true);

  const attendeeNames = (meeting.attendees || []).map((a) => a.name).filter(Boolean);
  const [speakers, setSpeakers] = useState(attendeeNames.length ? attendeeNames : ["Speaker 1", "Speaker 2"]);
  const [activeSpeaker, setActiveSpeaker] = useState(attendeeNames[0] || "Speaker 1");
  const [newSpeaker, setNewSpeaker] = useState("");
  const speakerRef = useRef(activeSpeaker);
  useEffect(() => { speakerRef.current = activeSpeaker; }, [activeSpeaker]);
  const addSpeaker = () => {
    const n = newSpeaker.trim();
    if (n && !speakers.includes(n)) { setSpeakers([...speakers, n]); setActiveSpeaker(n); }
    setNewSpeaker("");
  };

  const mrRef = useRef(null);
  const chunkBuf = useRef([]);
  const streamRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const recogRef = useRef(null);
  const rafRef = useRef(null);
  const tsRef = useRef(null);
  const transcriptRef = useRef(null);

  const elapsedFmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  const meterLoop = useCallback(() => {
    const analyser = analyserRef.current;
    if (!analyser) return;
    const data = new Uint8Array(analyser.fftSize);
    analyser.getByteTimeDomainData(data);
    let sum = 0;
    for (let i = 0; i < data.length; i++) { const v = (data[i] - 128) / 128; sum += v * v; }
    const rms = Math.sqrt(sum / data.length);
    setLevel(Math.min(100, Math.round(rms * 320)));
    rafRef.current = requestAnimationFrame(meterLoop);
  }, []);

  const startRecording = async () => {
    try {
      const mic = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
      let mixed = mic;
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const dest = ctx.createMediaStreamDestination();
      ctx.createMediaStreamSource(mic).connect(dest);
      if (withTab) {
        try {
          const tab = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: true });
          if (tab.getAudioTracks().length) ctx.createMediaStreamSource(tab).connect(dest);
          streamRef.current = tab;
        } catch { /* user cancelled tab share */ }
      }
      mixed = dest.stream;
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      ctx.createMediaStreamSource(mic).connect(analyser);
      audioCtxRef.current = ctx; analyserRef.current = analyser;

      chunkBuf.current = [];
      const mr = new MediaRecorder(mixed, { mimeType: MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "" });
      mr.ondataavailable = (e) => { if (e.data.size > 0) { chunkBuf.current.push(e.data); setChunks((c) => c + 1); } };
      mr.start(2000);
      mrRef.current = mr;
      micStream(mic);

      setState("recording");
      rafRef.current = requestAnimationFrame(meterLoop);
      tsRef.current = setInterval(() => {
        setElapsed((e) => e + 1);
        setLevel((lv) => {
          const sc = Math.max(45, Math.min(99, Math.round(92 - (lv < 4 ? 18 : 0) - (lv > 92 ? 12 : 0))));
          setScore(sc); setSpark((sp) => [...sp.slice(-39), sc]);
          return lv;
        });
      }, 1000);
      startSpeech();
    } catch (e) {
      toast.error("Microphone access denied. Please allow mic to record.");
    }
  };

  const micRef = useRef(null);
  const micStream = (s) => { micRef.current = s; };

  const startSpeech = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { setSpeechOk(false); return; }
    const recog = new SR();
    recog.continuous = true; recog.interimResults = true; recog.lang = "en-US";
    recog.onresult = (ev) => {
      let intt = "";
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        const r = ev.results[i];
        if (r.isFinal) {
          const text = r[0].transcript.trim();
          if (text) {
            setLines((l) => [...l, { s: speakerRef.current, ts: elapsedFmt(tsRef.now || 0), t: text }]);
            const ins = detectInsight(text);
            if (ins) setInsights((p) => [...p, ins]);
          }
        } else intt += r[0].transcript;
      }
      setInterim(intt);
    };
    recog.onend = () => { if (mrRef.current && mrRef.current.state === "recording") { try { recog.start(); } catch { /* */ } } };
    try { recog.start(); } catch { /* */ }
    recogRef.current = recog;
  };

  const pause = () => { mrRef.current?.pause(); try { recogRef.current?.stop(); } catch {} clearInterval(tsRef.current); cancelAnimationFrame(rafRef.current); setState("paused"); };
  const resume = () => { mrRef.current?.resume(); startSpeech(); rafRef.current = requestAnimationFrame(meterLoop); tsRef.current = setInterval(() => setElapsed((e) => e + 1), 1000); setState("recording"); };

  const cleanup = () => {
    clearInterval(tsRef.current); cancelAnimationFrame(rafRef.current);
    try { recogRef.current && (recogRef.current.onend = null, recogRef.current.stop()); } catch {}
    try { micRef.current?.getTracks().forEach((t) => t.stop()); } catch {}
    try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch {}
    try {
      const ctx = audioCtxRef.current;
      if (ctx && ctx.state !== "closed") ctx.close().catch(() => {});
      audioCtxRef.current = null;
    } catch {}
  };

  const stop = async () => {
    const mr = mrRef.current;
    if (!mr) { cleanup(); setState("idle"); return; }
    setState("paused");
    const done = new Promise((res) => { mr.onstop = res; });
    mr.stop();
    await done;
    cleanup();
    const blob = new Blob(chunkBuf.current, { type: "audio/webm" });
    setState("idle");
    if (blob.size < 2000) { toast.message("Recording stopped", { description: "Too short to transcribe." }); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", new File([blob], "meeting.webm", { type: "audio/webm" }));
      fd.append("engine", "whisper-1");
      const res = await api.post(`/meetings/${mid}/transcribe`, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 180000 });
      if (res.data.job_id) await pollJob(res.data.job_id);
      toast.success("Recording transcribed — opening Review");
      refresh?.();
      setActive("review");
    } catch (e) { toast.error(e?.response?.data?.detail || e.message || "Transcription failed"); }
    finally { setUploading(false); }
  };

  useEffect(() => { tsRef.now = elapsed; }, [elapsed]);
  useEffect(() => { transcriptRef.current?.scrollTo(0, transcriptRef.current.scrollHeight); }, [lines, interim]);
  useEffect(() => () => cleanup(), []);

  const currentAgenda = meeting.agenda?.[Math.min(Math.floor(lines.length / 3), (meeting.agenda?.length || 1) - 1)];

  if (!consent) {
    return (
      <div className="max-w-xl mx-auto rounded-xl border bg-card p-8 text-center">
        <div className="grid place-items-center w-14 h-14 rounded-xl bg-[#0f2557]/10 dark:bg-[#29abe2]/15 text-[#0f2557] dark:text-[#29abe2] mx-auto"><ShieldCheck size={26} /></div>
        <h3 className="text-lg font-semibold mt-4">Recording Consent</h3>
        <p className="text-sm text-muted-foreground mt-2">This session will be recorded and transcribed to prepare project deliverables, in line with the DPDP Act 2023. A visible recording indicator stays on throughout. Please confirm all attendees consent.</p>
        <div className="text-left text-xs bg-secondary rounded-lg p-3 mt-4 text-muted-foreground">Suggested script: "With your permission, I'll record this session so we can produce accurate requirements and minutes. The recording is confidential to this engagement."</div>
        <label className="flex items-center gap-2 justify-center mt-4 text-sm cursor-pointer"><Checkbox checked={withTab} onCheckedChange={setWithTab} data-testid="tab-audio-checkbox" /> Also capture meeting tab audio (online meetings)</label>
        <label className="flex items-center gap-2 justify-center mt-3 text-sm cursor-pointer"><Checkbox checked={consent} onCheckedChange={setConsent} data-testid="consent-checkbox" /> Consent captured from all attendees</label>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="rounded-xl border bg-card p-4 flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          {state === "recording" && <span className="flex items-center gap-1.5 text-red-500 text-sm font-medium"><Radio size={15} className="animate-pulse" /> REC</span>}
          <span className="font-mono text-lg font-semibold text-foreground">{elapsedFmt(elapsed)}</span>
        </div>
        <div className="flex gap-2">
          {state === "idle" && <Button onClick={startRecording} disabled={uploading} className="gap-2 bg-red-500 hover:bg-red-600" data-testid="start-recording">{uploading ? <Loader2 className="animate-spin" size={15} /> : <Play size={15} />} {uploading ? "Transcribing…" : "Start"}</Button>}
          {state === "recording" && <Button variant="outline" onClick={pause} className="gap-2" data-testid="pause-recording"><Pause size={15} /> Pause</Button>}
          {state === "paused" && <Button onClick={resume} className="gap-2"><Play size={15} /> Resume</Button>}
          {state !== "idle" && <Button variant="outline" onClick={stop} className="gap-2" data-testid="stop-recording"><Square size={15} /> Stop & transcribe</Button>}
        </div>
        {withTab && <span className="flex items-center gap-1 text-xs text-muted-foreground"><Monitor size={13} /> tab audio</span>}
        <div className="flex items-center gap-2 flex-1 min-w-[160px]">
          <Mic size={15} className="text-muted-foreground" />
          <div className="h-2 flex-1 bg-secondary rounded-full overflow-hidden"><div className="h-full rounded-full transition-all" style={{ width: `${level}%`, background: level > 88 ? "#ef4444" : "#29abe2" }} /></div>
        </div>
        <Button variant="ghost" size="sm" onClick={() => setActive("review")} className="gap-2 text-[#29abe2]" data-testid="go-review">Skip to upload <ArrowRight size={15} /></Button>
      </div>

      {!speechOk && <div className="text-xs text-[#a6821a] dark:text-[#e0b838] flex items-center gap-1.5"><AlertTriangle size={13} /> Live captions need Chrome/Edge. Recording still works and is transcribed accurately on Stop.</div>}

      <div className="grid lg:grid-cols-[260px_1fr_300px] gap-4">
        <div className="rounded-xl border bg-card p-4">
          <div className="text-[11px] font-semibold tracking-[0.18em] text-[#29abe2] uppercase mb-3">Agenda</div>
          <div className="space-y-2">
            {(meeting.agenda || []).map((a, i) => {
              const active = a === currentAgenda && state === "recording";
              return (
                <div key={i} className={`p-2.5 rounded-lg border text-sm ${active ? "border-[#29abe2] bg-[#29abe2]/5" : "border-transparent"}`}>
                  <div className="flex items-center justify-between"><span className={active ? "font-medium text-foreground" : "text-muted-foreground"}>{a.title || `Item ${i + 1}`}</span>{active && <Activity size={13} className="text-[#29abe2]" />}</div>
                  <div className="text-[10px] text-muted-foreground">{a.minutes} min</div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="rounded-xl border bg-card p-4 flex flex-col">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[11px] font-semibold tracking-[0.18em] text-[#29abe2] uppercase">Live Captions</div>
            <span className="text-[10px] text-muted-foreground">Speaking: <b className="text-foreground">{activeSpeaker}</b></span>
          </div>
          <div className="flex flex-wrap gap-1.5 mb-3" data-testid="speaker-bar">
            {speakers.map((sp) => (
              <button key={sp} onClick={() => setActiveSpeaker(sp)} data-testid={`speaker-${sp}`}
                className={`text-xs px-2.5 py-1 rounded-full border transition ${activeSpeaker === sp ? "bg-[#0f2557] text-white border-[#0f2557] dark:bg-[#29abe2] dark:border-[#29abe2]" : "hover:border-[#29abe2]"}`}>{sp}</button>
            ))}
            <input value={newSpeaker} onChange={(e) => setNewSpeaker(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addSpeaker()}
              placeholder="+ add speaker" data-testid="add-speaker-input"
              className="text-xs px-2.5 py-1 rounded-full border bg-background outline-none focus:border-[#29abe2] w-28" />
          </div>
          <div ref={transcriptRef} className="flex-1 overflow-y-auto space-y-3 max-h-[440px]" data-testid="live-transcript">
            {lines.length === 0 && !interim && <p className="text-sm text-muted-foreground">{state === "recording" ? "Listening…" : "Press Start to record and see live captions."}</p>}
            {lines.map((l, i) => (
              <div key={i} className="animate-fade-up">
                <div className="flex items-center gap-2 text-xs"><span className="font-semibold text-[#0f2557] dark:text-[#29abe2]">{l.s}</span><span className="text-muted-foreground font-mono">{l.ts}</span></div>
                <p className="text-sm text-foreground mt-0.5">{l.t}</p>
              </div>
            ))}
            {interim && <p className="text-sm text-muted-foreground italic">{interim}</p>}
          </div>
        </div>

        <div className="space-y-4">
          <div className="rounded-xl border bg-card p-4">
            <div className="text-[11px] font-semibold tracking-[0.18em] text-[#29abe2] uppercase mb-2">Capture Quality</div>
            <div className="flex items-center gap-3">
              <Gauge score={score} />
              <div className="flex-1 text-xs space-y-1.5 text-muted-foreground">
                <div className="flex justify-between"><span>Input level</span><span className="text-foreground font-medium">{level}%</span></div>
                <div className="flex justify-between"><span>Chunks</span><span className="text-emerald-500 font-medium">{chunks} ✓</span></div>
                <div className="flex justify-between"><span>Captions</span><span className="text-foreground font-medium">{lines.length}</span></div>
                <div className="flex justify-between"><span>Elapsed</span><span className="text-foreground font-medium">{elapsedFmt(elapsed)}</span></div>
              </div>
            </div>
            <div className="flex items-end gap-0.5 h-10 mt-3">
              {spark.map((v, i) => <div key={i} className="flex-1 rounded-sm" style={{ height: `${v}%`, background: v >= 80 ? "#10b981" : v >= 55 ? "#c9a227" : "#ef4444", opacity: 0.4 + (i / spark.length) * 0.6 }} />)}
            </div>
            {level < 4 && state === "recording" && <div className="flex items-center gap-1.5 text-xs text-[#a6821a] dark:text-[#e0b838] mt-2"><AlertTriangle size={13} /> Very low input — check your microphone</div>}
          </div>

          <div className="rounded-xl border bg-card p-4">
            <div className="text-[11px] font-semibold tracking-[0.18em] text-[#29abe2] uppercase mb-3">Live Insights</div>
            <div className="space-y-2 max-h-[220px] overflow-y-auto" data-testid="live-insights">
              {insights.length === 0 && <p className="text-xs text-muted-foreground">Requirements, decisions and risks detected from captions appear here.</p>}
              {insights.map((ins, i) => (
                <div key={i} className={`border-l-2 rounded-r-lg p-2.5 text-xs ${TYPE_STYLE[ins.type]} animate-fade-up`}>
                  <div className="flex items-center justify-between"><span className="uppercase tracking-wider font-semibold text-[10px] text-muted-foreground">{ins.type}</span><Pin size={11} className="text-muted-foreground" /></div>
                  <p className="text-foreground mt-1">{ins.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
